import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" aria-label="Avirova Technologies home">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 bg-ink-900">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
        <path d="M5 13.5 10 18 19 6" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className="font-display text-lg font-bold tracking-tight text-zinc-100">
      AVIROVA
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-black/40 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />
        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `group relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-volt" : "text-zinc-400 hover:text-zinc-100"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group flex items-center gap-1.5 rounded-full bg-volt px-5 py-2 text-[13px] font-semibold text-black transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            Get a Free QA Consultation
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-800 text-zinc-200 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="overflow-hidden border-b border-zinc-800 bg-ink-950/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {[{ to: "/", label: "Home" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-3 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-volt" : "text-zinc-200"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-volt px-5 py-3.5 text-sm font-semibold text-black"
              >
                Get a Free QA Consultation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
